import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  calculateReturnDate,
  countLeaveDays,
  mergeLeavePlan,
  getPublicHolidays,
} from "../../../store/leave/actions";
import { connect } from "react-redux";
import moment from "moment";
import ConfirmationDialog from "../../common/components/ConfirmationDialog";
import {
  getDateOnly,
  isHoliday,
  isWeekend,
} from "../../common/components/Utils";

const initialLoadingState = {
  countingDays: false,
  isLoadingData: true,
  isSubmitting: false,
};

const MergeAnnualLeaveForm = (props) => {
  const {
    showForm,
    setCloseForm,
    allEmployeeLeavePlans,
    loading,
    user,
    selectedFiscalYear,
    publicHolidays,
    countLeaveDays,
    mergeLeavePlan,
    getPublicHolidays,
    calculateReturnDate,
  } = props;

  const [loadingState, setLoadingState] = useState(initialLoadingState);
  const [selectedLeave, setSelectedLeave] = useState([]);
  const [formData, setFormData] = useState({
    startDate: null,
    endDate: null,
    comment: "",
  });
  const [maxDate, setMaxDate] = useState({
    start: new Date(selectedFiscalYear?.endOn),
    end: new Date(moment(selectedFiscalYear.endOn).add(6, "months")),
  });
  const [errors, setErrors] = useState({
    startDateHasError: false,
    endDateHasError: false,
    messageHasError: false,
    messageErrorMessage: "",
  });
  const [plannedDays, setPlannedDays] = useState(null);
  const [mergedDays, setMergedDays] = useState(null);
  const [confirmMerge, setConfirmMerge] = useState(false);

  useEffect(() => {
    if (!!showForm) {
      if (publicHolidays.length === 0) {
        getPublicHolidays((status) =>
          setLoadingState((prev) => ({ ...prev, isLoadingData: status }))
        );
      } else {
        setLoadingState((prev) => ({ ...prev, isLoadingData: false }));
      }
    }

    return () => {
      setLoadingState(initialLoadingState);
    };
  }, [showForm]);

  useEffect(() => {
    if (!!selectedLeave[0]) {
      const days = selectedLeave.reduce((acc, obj) => ({
        numOfDays: acc.numOfDays + obj.numOfDays,
      }));
      setMergedDays(days.numOfDays);
    } else if (mergedDays !== 0) {
      setMergedDays(0);
    }
  }, [selectedLeave]);

  useEffect(() => {
    if (!!formData.startDate) {
      calculateReturnDate(
        {
          startDate: formData.startDate,
          numOfDays: mergedDays,
        },
        (status, data) => {
          setLoadingState((prev) => ({ ...prev, countingDays: loading }));
          if (!loading && data) {
            setMaxDate((prev) => ({
              ...prev,
              end: moment(data).format("YYYY-MM-DD"),
            }));
          }
        }
      );
    } else {
      setMaxDate((prev) => ({
        ...prev,
        end: new Date(moment(selectedFiscalYear.endOn).add(6, "months")),
      }));
    }
  }, [formData.startDate]);

  const handleDatesToDisable = (date) => {
    const isContractual = false; //[18, 19, 20, 21].includes(+user.leaveDays);
    if (isWeekend(date) && isContractual) return true;
    if (isHoliday(publicHolidays, date) && isContractual) return true;

    let _leavePlans = (allEmployeeLeavePlans || []).filter((item) =>
      [1, 6, 7, 9, 11, 14, 15, 18].includes(item.statusId)
    );

    const dates = _leavePlans.map(({ startDate, endDate }) => {
      const start = new Date(startDate);
      const end = new Date(endDate);
      return date >= start && date <= end;
    });
    return dates.includes(true);
  };

  const onClose = () => {
    // clean all states before close

    //   close form
    setCloseForm();
  };

  const handleCountLeaveDays = (endDate = null) => {
    if (!!formData.startDate && !!endDate) {
      countLeaveDays(
        {
          startDate: formData.startDate,
          endDate: endDate,
          leavePlanId: "",
          employeePositionId: selectedLeave[0]?.employeePositionId || "",
          isPostponed: true,
        },
        (loading, data) => {
          setLoadingState((prev) => ({ ...prev, countingDays: loading }));
          if (!loading) setPlannedDays(() => data);
        }
      );
    }
  };

  const onSubmitRequest = () => {
    mergeLeavePlan(
      {
        startDate: formData.startDate,
        endDate: formData.endDate,
        numOfDays: plannedDays?.days || 0,
        comment: formData.comment,
        employeePositionId: selectedLeave[0].employeePositionId,
        selectedLeavePlans: selectedLeave.map(({ id, postponedId }) => ({
          leavePlanId: id,
          postponedId,
        })),
      },
      (status, res) => {
        setLoadingState((prev) => ({ ...prev, isSubmitting: status }));
        if (!status && res) {
          onClose();
        }
      }
    );
  };

  return (
    <>
      <Dialog
        onClose={onClose}
        aria-labelledby="customized-dialog-title"
        open={showForm}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className="text-dark pb-2">
          <Typography
            variant="overline"
            className="text-truncate"
            display="block"
            style={{ maxWidth: "90%" }}
          >
            Merge ANNUAL LEAVE |{" "}
            <strong className="text-primary">{selectedFiscalYear?.name}</strong>
          </Typography>

          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <span className="material-icons">close</span>
          </IconButton>
        </DialogTitle>

        <DialogContent className="py-1">
          <Typography
            variant="overline"
            className="font-weight-bold"
            display="block"
          >
            Select Leave To Merge
          </Typography>
          <Autocomplete
            fullWidth
            multiple
            disableCloseOnSelect
            disablePortal
            size="small"
            value={selectedLeave || null}
            options={(allEmployeeLeavePlans || []).filter(
              (item) => ![1, 6, 7, 9, 11, 14, 15, 18].includes(item.statusId)
            )}
            getOptionLabel={(option) =>
              `${
                moment(option.startDate).format("DD MMMM, YYYY") +
                "-" +
                moment(option.endDate).format("DD MMMM, YYYY")
              }`
            }
            // isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <div {...props}>
                <div className="d-flex align-items-center">
                  <span
                    className="badge badge-secondary text-left text-uppercase"
                    style={{ minWidth: "140px" }}
                  >
                    <span
                      className="badge badge-secondary bg-light text-secondary font-weight-bold"
                      style={{ minWidth: "100%", fontSize: "12px" }}
                    >
                      {moment(option.startDate).format("DD MMMM, YYYY")}
                    </span>
                  </span>
                  <span className="font-weight-bold mx-2">-</span>
                  <span
                    className="badge badge-secondary text-left text-uppercase"
                    style={{ minWidth: "140px" }}
                  >
                    <span
                      className="badge badge-secondary bg-light text-secondary font-weight-bold"
                      style={{ minWidth: "100%", fontSize: "12px" }}
                    >
                      {moment(option.endDate).format("DD MMMM, YYYY")}
                    </span>
                  </span>

                  <span
                    className="badge badge-secondary text-left text-uppercase ml-3"
                    style={{ minWidth: "60px" }}
                  >
                    <span
                      className="badge badge-secondary bg-light text-secondary font-weight-bold"
                      style={{ fontSize: "12px" }}
                    >
                      {option.numOfDays}
                    </span>{" "}
                    <span style={{ fontSize: "12px" }}>Days</span>
                  </span>
                </div>
                <Divider />
                <Divider />
              </div>
            )}
            onChange={(event, newValue) => {
              setSelectedLeave(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Responsible Supervisor"
                placeholder="Select Responsible Supervisor"
              />
            )}
            loading={
              loadingState.isLoadingData ||
              loadingState.isSubmitting ||
              loadingState.countingDays
            }
            loadingText="Loading ..."
          />

          <Typography
            variant="overline"
            className="font-weight-bold mt-2"
            display="block"
          >
            Selected Leave To Merge
          </Typography>
          <div>
            {!!selectedLeave.length ? (
              selectedLeave.map((leave) => (
                <div
                  className="d-flex align-items-center mb-2"
                  key={leave.id + "-" + leave.postponedId}
                >
                  <span className="font-weight-bold">From</span>
                  <input
                    type="text"
                    value={moment(leave.startDate).format("DD MMMM, YYYY")}
                    className="mx-1 text-center rounded px-2 py-1"
                    disabled
                  />
                  <span className="font-weight-bold">To</span>
                  <input
                    type="text"
                    value={moment(leave.endDate).format("DD MMMM, YYYY")}
                    className="ml-1 mr-1 text-center rounded px-2 py-1"
                    disabled
                  />

                  <span className="badge badge-secondary text-left text-uppercase">
                    <span className="badge badge-secondary bg-light text-secondary font-weight-bold">
                      {mergedDays}
                    </span>{" "}
                    <span>Days</span>
                  </span>
                </div>
              ))
            ) : (
              <Alert severity="warning">
                Please select leave to merge first.
              </Alert>
            )}
          </div>

          <Divider className="my-4">
            <Chip label="Select New Date" size="small" />
          </Divider>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <div className="mt-1">
              <div className="d-flex align-items-center">
                <DesktopDatePicker
                  label="Leave Start Date"
                  inputFormat="dd/MM/yyyy"
                  disabled={
                    loading ||
                    loadingState.countingDays ||
                    loadingState.isSubmitting ||
                    loadingState.isLoadingData ||
                    !selectedLeave[0]
                  }
                  value={formData.startDate}
                  onChange={(date) => {
                    setFormData({
                      ...formData,
                      startDate: getDateOnly(date),
                      endDate: null,
                    });
                    setPlannedDays((prev) => null);

                    setErrors({
                      ...errors,
                      startDateHasError: false,
                    });
                  }}
                  minDate={
                    selectedFiscalYear && new Date(selectedFiscalYear?.startOn)
                  }
                  maxDate={maxDate.start}
                  renderInput={(params) => (
                    <TextField
                      onKeyDown={(e) => e.preventDefault()}
                      required={true}
                      fullWidth
                      size="small"
                      {...params}
                    />
                  )}
                  shouldDisableDate={handleDatesToDisable}
                  disablePast={true}
                />

                <span className="mx-2" style={{ fontSize: "30px" }}>
                  -
                </span>

                <DesktopDatePicker
                  label="Leave End Date"
                  inputFormat="dd/MM/yyyy"
                  disabled={
                    !formData.startDate ||
                    loading ||
                    loadingState.countingDays ||
                    loadingState.isSubmitting ||
                    loadingState.isLoadingData
                  }
                  defaultCalendarMonth={formData.startDate || new Date()}
                  value={formData.endDate}
                  onChange={(date) => {
                    setFormData({
                      ...formData,
                      endDate: getDateOnly(date),
                    });
                    setPlannedDays(null);
                    setErrors({
                      ...errors,
                      endDateHasError: false,
                    });
                    handleCountLeaveDays(getDateOnly(date));
                  }}
                  renderInput={(params) => (
                    <TextField
                      onKeyDown={(e) => e.preventDefault()}
                      required={true}
                      fullWidth
                      size="small"
                      {...params}
                    />
                  )}
                  minDate={formData.startDate}
                  maxDate={maxDate.end}
                  shouldDisableDate={handleDatesToDisable}
                  disablePast={true}
                />
              </div>

              {!!plannedDays && (
                <div className="d-flex align-items-center text-uppercase">
                  <span
                    className={`badge ${
                      plannedDays.days > 0 && plannedDays.days <= mergedDays
                        ? "badge-secondary"
                        : "badge-danger"
                    } text-left text-uppercase mr-1`}
                    style={{ minWidth: "132px" }}
                    title="Planned days of leave"
                  >
                    <span
                      className={`badge badge-light${
                        plannedDays.days > 0 && plannedDays.days <= mergedDays
                          ? " bg-secondary"
                          : " bg-danger"
                      } text-light`}
                      style={{ fontSize: "12px" }}
                    >
                      {plannedDays.days}/{mergedDays}
                    </span>{" "}
                    Planned days
                  </span>
                  <span
                    className="badge badge-warning text-left text-uppercase"
                    style={{ minWidth: "132px" }}
                    title="Other staff with same period of leave"
                  >
                    <span
                      className="badge badge-light bg-warning text-light"
                      // style={{ fontSize: "12px" }}
                    >
                      {plannedDays.otherEmployeesWithLeave}
                    </span>{" "}
                    Other staff
                  </span>
                </div>
              )}
            </div>
          </LocalizationProvider>

          <TextField
            error={errors.messageHasError}
            helperText={errors.messageErrorMessage}
            fullWidth
            size="small"
            multiline
            rows={4}
            // autoFocus={assignment.targetShare === 0}
            name="employeeMessage"
            label="Message"
            variant="outlined"
            placeholder={`Message (Required)`}
            className="mb-3 mt-3"
            value={formData.comment}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"></InputAdornment>
              ),
            }}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                comment: e.target.value,
              }));
              setErrors({
                ...errors,
                messageHasError: false,
                messageErrorMessage: "",
              });
            }}
            disabled={
              loading ||
              loadingState.countingDays ||
              !plannedDays ||
              plannedDays.days > mergedDays ||
              loadingState.isLoadingData ||
              loadingState.isSubmitting
            }
          />
        </DialogContent>
        <DialogActions className="py-2 my-1">
          <div className="d-flex justify-content-center w-100">
            <button
              onClick={setConfirmMerge}
              type="button"
              className="btn btn-sm btn-primary text-uppercase px-5"
              disabled={
                loading ||
                !plannedDays?.days ||
                (plannedDays?.days || 0) !== mergedDays ||
                loadingState.countingDays ||
                loadingState.isLoadingData ||
                loadingState.isSubmitting
              }
            >
              {loadingState.isSubmitting ? "Wait..." : "Submit"}
            </button>
          </div>
        </DialogActions>
      </Dialog>

      {confirmMerge && (
        <ConfirmationDialog
          confirmationDialog={true}
          message={`Are you sure you want to merge these ${selectedLeave.length} leaves?`}
          setConfirmationDialog={() => setConfirmMerge(false)}
          onYes={onSubmitRequest}
        />
      )}
    </>
  );
};

const mapStateToProps = ({
  loading,
  user,
  selectedFiscalYear,
  publicHolidays,
}) => {
  return {
    loading,
    user,
    selectedFiscalYear,
    publicHolidays,
  };
};
export default connect(mapStateToProps, {
  countLeaveDays,
  mergeLeavePlan,
  getPublicHolidays,
  calculateReturnDate,
})(MergeAnnualLeaveForm);
